import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageLayout from '@/components/feature/PageLayout';
import ClientLogosStrip from '@/components/feature/ClientLogosStrip';
import EngagementModels from '@/pages/home/components/EngagementModels';
import { services } from '@/mocks/services';

const generateServicesSchema = () => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": services.map((s, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Service",
        "name": s.title,
        "description": s.fullDescription,
        "provider": {
          "@type": "Organization",
          "name": "Thoughtic Solutions"
        },
        "serviceType": s.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      }
    }))
  });
};

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const categoryColors: Record<string, { bg: string; text: string; border: string; iconBg: string }> = {
  'strategy-design': { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200', iconBg: 'bg-rose-500' },
  development: { bg: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200', iconBg: 'bg-sky-500' },
  'cloud-ai': { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', iconBg: 'bg-violet-500' },
  'quality-support': { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', iconBg: 'bg-emerald-500' },
  talent: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', iconBg: 'bg-amber-500' },
};

function PageHero() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div className="pt-28 pb-16 md:pt-36 md:pb-0 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dark%20corporate%20service%20background%20with%20subtle%20warm%20amber%20and%20emerald%20green%20light%20accents%2C%20deep%20navy%20blue%20tones%2C%20elegant%20geometric%20shapes%2C%20minimalist%20professional%20composition%20suitable%20for%20website%20hero%20banner%20with%20white%20text%20overlay%2C%20soft%20gradient%20lighting%20effects%2C%20premium%20feel&width=1600&height=900&seq=services-hero-1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/60 via-navy-900/70 to-navy-900/90"></div>
      </div>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center pb-5"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-4 md:mb-6"
          >
            <i className="ri-briefcase-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">What We Offer</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight mb-4 md:mb-6"
          >
            Two Ways We
            <br />
            <span className="text-primary-400">Deliver Value</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-white/70 max-w-3xl mx-auto"
          >
            We build software end-to-end — and we also place world-class talent directly on your team. Choose the model that fits your goals, or blend both.
          </motion.p>

          {/* Two Pillars Quick Nav */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 md:mt-10"
          >
            <a
              href="#software-development"
              className="px-6 py-3 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-code-box-line"></i>
              Software Development
            </a>
            <a
              href="#talent-solutions"
              className="px-6 py-3 bg-white/10 border border-white/20 text-white text-sm font-semibold rounded-xl hover:bg-white/15 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <i className="ri-user-star-line"></i>
              Talent Solutions
            </a>
          </motion.div>
        </motion.div>
                        <ClientLogosStrip />
      </div>
    </div>
  );
}

function WhyOurServices() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const values = [
    {
      icon: 'ri-compass-discover-line',
      title: 'Build or Hire',
      desc: 'Need a product built? We engineer end-to-end. Need talent fast? We place vetted professionals in 48 hours.',
      stat: '15',
      statLabel: 'service verticals',
    },
    {
      icon: 'ri-code-box-line',
      title: 'Full-Stack Engineering',
      desc: 'Strategy, design, development, cloud, AI, and QA — all under one roof with consistent quality standards.',
      stat: '11',
      statLabel: 'dev services',
    },
    {
      icon: 'ri-user-star-line',
      title: 'Vetted Talent Network',
      desc: 'Pre-screened engineers, designers, QA, and DevOps specialists ready to join your team on demand.',
      stat: '200+',
      statLabel: 'professionals in network',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality & Compliance',
      desc: 'ISO 27001, SOC 2, and GDPR-ready processes. Security, testing, and compliance built into every delivery.',
      stat: '99.9%',
      statLabel: 'uptime SLA',
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-award-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Why Our Services</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Built for Speed
            <span className="text-primary-600"> and Scale</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Whether you need a product strategy workshop, a full engineering team, or a single specialist — we deliver with the same rigor, transparency, and commitment to outcomes.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {values.map((v, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 + index * 0.08, ...springTransition }}
              className="group bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 rounded-xl mb-4 group-hover:scale-110 transition-transform duration-300">
                <i className={`${v.icon} text-xl text-white`}></i>
              </div>
              <div className="mb-3">
                <span className="text-2xl md:text-3xl font-bold text-primary-600">{v.stat}</span>
                <p className="text-xs text-gray-500 mt-0.5">{v.statLabel}</p>
              </div>
              <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2">{v.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PillarSection({
  id,
  badgeIcon,
  badgeLabel,
  title,
  titleAccent,
  description,
  serviceCategories: categories,
  bgShade,
}: {
  id: string;
  badgeIcon: string;
  badgeLabel: string;
  title: string;
  titleAccent: string;
  description: string;
  serviceCategories: { id: string; label: string }[];
  bgShade: string;
}) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.06 });
  const categoryServices = services.filter((s) =>
    categories.some((c) => c.id === s.category)
  );

  return (
    <section id={id} className={`py-16 md:py-24 ${bgShade} relative overflow-hidden`} ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-14"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className={`${badgeIcon} text-primary-600 w-5 h-5 flex items-center justify-center`}></i>
            <span className="text-sm font-medium text-gray-700">{badgeLabel}</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            {title}
            <br />
            <span className="text-primary-600">{titleAccent}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            {description}
          </motion.p>
        </motion.div>

        {/* Group by sub-category */}
        {categories.map((cat) => {
          const catServices = categoryServices.filter((s) => s.category === cat.id);
          if (catServices.length === 0) return null;
          const colors = categoryColors[cat.id] || { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', iconBg: 'bg-gray-500' };

          return (
            <div key={cat.id} className="mb-12 md:mb-16 last:mb-0">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: 0.3, ...springTransition }}
                className="flex items-center gap-3 mb-6 md:mb-8"
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${colors.iconBg}`}>
                  <i className="ri-folder-line text-white text-sm"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-navy-900">{cat.label}</h3>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {catServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 25, scale: 0.97 }}
                    animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: 0.35 + index * 0.06, ...springTransition, duration: 0.5 }}
                    className="group bg-white rounded-2xl border border-gray-100 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/5 transition-all duration-500 overflow-hidden"
                  >
                    <div className="p-5 md:p-6">
                      <div className="flex items-start gap-3 mb-4">
                        <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg ${colors.iconBg}`}>
                          <i className={`${service.icon} text-white text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-base md:text-lg font-bold text-navy-900 leading-snug">
                            {service.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm font-bold text-primary-600">{service.stat}</span>
                            <span className="text-xs text-gray-400">{service.statLabel}</span>
                          </div>
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {service.fullDescription}
                      </p>

                      <div className="space-y-2 mb-5">
                        {service.features.map((feature) => (
                          <div key={feature} className="flex items-start gap-2">
                            <div className="w-4 h-4 flex items-center justify-center rounded-full bg-primary-50 flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-[10px] text-primary-600"></i>
                            </div>
                            <span className="text-xs text-gray-600">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <a
                        href="#cta"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer group/link"
                      >
                        Discuss your needs
                        <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ServicesCTA() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <section id="cta" className="py-16 md:py-24 bg-navy-900 relative overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
            <i className="ri-message-3-line text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-white/80">Ready to Start?</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white leading-tight mb-4 md:mb-6">
            Build Software <span className="text-primary-400">or</span> Hire Talent
          </h2>
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10">
            Whether you need a complete product built or a single engineer to join your team — we will recommend the right approach and get you started fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/software-development"
              className="px-8 py-4 bg-primary-500 text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Start a Project
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform inline-block"></i>
            </Link>
            <Link
              to="/talent-solutions"
              className="px-8 py-4 border-2 border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/10 hover:border-white/50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Browse Talent Pool
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = generateServicesSchema();
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);

  return (
    <PageLayout
      title="Software Development & Talent Solutions"
      description="Explore Thoughtic Solutions' dual offering: end-to-end software development (strategy, design, dev, cloud, AI, QA) and flexible talent solutions (dedicated teams, contract staffing, offshore)."
      keywords="software development services, IT talent solutions, dedicated engineering teams, offshore developers, staff augmentation, custom software development, cloud engineering, AI solutions, QA testing services"
    >
      <PageHero />
      <WhyOurServices />

      {/* Pillar 1: Software Development Services */}
      <PillarSection
        id="software-development"
        badgeIcon="ri-code-box-line"
        badgeLabel="Software Development"
        title="End-to-End"
        titleAccent="Software Development"
        description="From product discovery and UI/UX design to custom engineering, cloud infrastructure, AI, and quality assurance — we handle the full product lifecycle."
        serviceCategories={[
          { id: 'strategy-design', label: 'Strategy & Design' },
          { id: 'development', label: 'Development' },
          { id: 'cloud-ai', label: 'Cloud & AI' },
          { id: 'quality-support', label: 'Quality & Support' },
        ]}
        bgShade="bg-white"
      />

      {/* Pillar 2: Staff Augmentation & Talent Solutions */}
      <PillarSection
        id="talent-solutions"
        badgeIcon="ri-user-star-line"
        badgeLabel="Talent Solutions"
        title="Flexible Talent"
        titleAccent="On Demand"
        description="Hire vetted engineers, designers, and specialists directly into your team — from a single contract hire to a fully managed offshore squad."
        serviceCategories={[
          { id: 'talent', label: 'Staff Augmentation & Talent' },
        ]}
        bgShade="bg-gray-50/50"
      />

      <EngagementModels />
      <ServicesCTA />
    </PageLayout>
  );
}